import React, { useEffect, useMemo, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Eye, EyeOff, Search, Filter, Download, User, MapPin, Clock, MessageSquare, Activity, AlertTriangle, TrendingUp, Calendar, Globe } from 'lucide-react';
import { PersonProfile, SessionRecord, EventRecord, ChatRecord, ConsentRecord } from '../types/analytics.types';
const FOUNDER_PASSWORD = '!Phat0909010138';
export const FounderPortal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'people' | 'sessions' | 'chats' | 'events'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPerson, setSelectedPerson] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  // Load all data from localStorage
  const allData = useMemo(() => {
    const consents: ConsentRecord[] = JSON.parse(localStorage.getItem('consent_record') || '[]');
    const people: PersonProfile[] = JSON.parse(localStorage.getItem('person_profile') || '[]');
    const sessions: SessionRecord[] = JSON.parse(localStorage.getItem('sessions') || '[]');
    const events: EventRecord[] = JSON.parse(localStorage.getItem('events') || '[]');
    const chats: ChatRecord[] = JSON.parse(localStorage.getItem('chats') || '[]');
    return {
      consents: Array.isArray(consents) ? consents : [consents].filter(Boolean),
      people: Array.isArray(people) ? people : [people].filter(Boolean),
      sessions,
      events,
      chats
    };
  }, [isAuthenticated]);
  // Keyboard shortcut
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        setIsOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  const handleAuth = () => {
    if (password === FOUNDER_PASSWORD) {
      setIsAuthenticated(true);
    } else {
      alert('Incorrect password');
      setPassword('');
    }
  };
  const handleExport = () => {
    const exportData = {
      exported_at: new Date().toISOString(),
      consents: allData.consents,
      people: allData.people,
      sessions: allData.sessions,
      events: allData.events,
      chats: allData.chats
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-export-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };
  // Filter data based on search
  const filteredPeople = allData.people.filter(person => person.name?.toLowerCase().includes(searchQuery.toLowerCase()) || person.role?.toLowerCase().includes(searchQuery.toLowerCase()) || person.company?.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredSessions = allData.sessions.filter(session => session.city?.toLowerCase().includes(searchQuery.toLowerCase()) || session.country?.toLowerCase().includes(searchQuery.toLowerCase()) || session.ip_address?.includes(searchQuery));
  const filteredChats = allData.chats.filter(chat => chat.user_message.toLowerCase().includes(searchQuery.toLowerCase()) || chat.ai_response.toLowerCase().includes(searchQuery.toLowerCase()));
  // Stats calculations
  const stats = {
    totalPeople: allData.people.length,
    totalSessions: allData.sessions.length,
    totalEvents: allData.events.length,
    totalChats: allData.chats.length,
    avgSessionDuration: allData.sessions.length > 0 ? Math.round(allData.sessions.reduce((sum, s) => sum + s.duration, 0) / allData.sessions.length) : 0,
    totalConsents: allData.consents.filter(c => c.agreed).length
  };
  return <>
      {/* Hidden Trigger */}
      <button onClick={() => setIsOpen(true)} className="fixed bottom-4 left-4 opacity-0 hover:opacity-100 transition-opacity duration-300 z-[9998] p-2 bg-red-600 text-white rounded-full shadow-lg" title="Founder Portal (Ctrl+Shift+P)" data-id="element-656">
        <Lock size={20} data-id="element-657" />
      </button>

      {/* Portal Modal */}
      <AnimatePresence data-id="element-658">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-id="element-659">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden border-2 border-red-500" data-id="element-660">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-600 to-pink-600 p-4 flex items-center justify-between text-white" data-id="element-661">
                <div className="flex items-center gap-3" data-id="element-662">
                  <Lock size={24} data-id="element-663" />
                  <div data-id="element-664">
                    <h2 className="text-xl font-bold" data-id="element-665">
                      Founder Analytics Portal
                    </h2>
                    <p className="text-red-100 text-xs" data-id="element-666">Owner Access Only</p>
                  </div>
                </div>
                <button onClick={() => {
              setIsOpen(false);
              setIsAuthenticated(false);
              setPassword('');
            }} className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded transition-colors" data-id="element-667">
                  <X size={20} data-id="element-668" />
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(95vh-80px)]" data-id="element-669">
                {!isAuthenticated ?
            // ========== AUTH SCREEN ==========
            <div className="p-8 max-w-md mx-auto" data-id="element-670">
                    <div className="text-center mb-6" data-id="element-671">
                      <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-672">
                        <Lock className="w-10 h-10 text-red-600 dark:text-red-400" data-id="element-673" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-674">
                        Founder Authentication
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm" data-id="element-675">
                        Enter your password to access the analytics portal
                      </p>
                    </div>

                    <div className="space-y-4" data-id="element-676">
                      <div className="relative" data-id="element-677">
                        <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAuth()} placeholder="Enter founder password" className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none" autoFocus data-id="element-678" />
                        <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-679">
                          {showPassword ? <EyeOff size={18} data-id="element-680" /> : <Eye size={18} data-id="element-681" />}
                        </button>
                      </div>

                      <button onClick={handleAuth} className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-red-600 to-pink-600 text-white font-bold hover:from-red-700 hover:to-pink-700 transition-all shadow-lg" data-id="element-682">
                        Unlock Portal
                      </button>
                    </div>
                  </div> :
            // ========== MAIN PORTAL ==========
            <div className="p-6" data-id="element-683">
                    {/* Top Actions */}
                    <div className="flex gap-4 mb-6" data-id="element-684">
                      <div className="flex-1 relative" data-id="element-685">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-686" />
                        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, role, company, location, IP..." className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 outline-none" data-id="element-687" />
                      </div>
                      <button onClick={handleExport} className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium flex items-center gap-2 transition-colors" data-id="element-688">
                        <Download size={18} data-id="element-689" />
                        Export All
                      </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 mb-6 overflow-x-auto" data-id="element-690">
                      {(['overview', 'people', 'sessions', 'chats', 'events'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${activeTab === tab ? 'bg-red-600 text-white shadow-lg' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700'}`} data-id="element-691">
                          {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>)}
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'overview' && <div className="space-y-6" data-id="element-692">
                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-693">
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800" data-id="element-694">
                            <User className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-2" data-id="element-695" />
                            <div className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-696">
                              {stats.totalPeople}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-697">
                              People
                            </div>
                          </div>
                          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800" data-id="element-698">
                            <Activity className="w-6 h-6 text-green-600 dark:text-green-400 mb-2" data-id="element-699" />
                            <div className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-700">
                              {stats.totalSessions}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-701">
                              Sessions
                            </div>
                          </div>
                          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800" data-id="element-702">
                            <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400 mb-2" data-id="element-703" />
                            <div className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-704">
                              {stats.totalChats}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-705">
                              Chats
                            </div>
                          </div>
                          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-800" data-id="element-706">
                            <TrendingUp className="w-6 h-6 text-orange-600 dark:text-orange-400 mb-2" data-id="element-707" />
                            <div className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-708">
                              {stats.totalEvents}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-709">
                              Events
                            </div>
                          </div>
                          <div className="bg-pink-50 dark:bg-pink-900/20 rounded-xl p-4 border border-pink-200 dark:border-pink-800" data-id="element-710">
                            <Clock className="w-6 h-6 text-pink-600 dark:text-pink-400 mb-2" data-id="element-711" />
                            <div className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-712">
                              {formatDuration(stats.avgSessionDuration)}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-713">
                              Avg Duration
                            </div>
                          </div>
                          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4 border border-indigo-200 dark:border-indigo-800" data-id="element-714">
                            <AlertTriangle className="w-6 h-6 text-indigo-600 dark:text-indigo-400 mb-2" data-id="element-715" />
                            <div className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-716">
                              {stats.totalConsents}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-717">
                              Consents
                            </div>
                          </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700" data-id="element-718">
                          <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white" data-id="element-719">
                            Recent Sessions
                          </h3>
                          <div className="space-y-3" data-id="element-720">
                            {allData.sessions.slice(-10).reverse().map(session => <div key={session.session_id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg" data-id="element-721">
                                  <div className="flex items-center gap-3" data-id="element-722">
                                    <MapPin className="w-4 h-4 text-gray-400" data-id="element-723" />
                                    <div data-id="element-724">
                                      <div className="font-medium text-sm text-gray-900 dark:text-white" data-id="element-725">
                                        {session.city}, {session.country}
                                      </div>
                                      <div className="text-xs text-gray-500" data-id="element-726">
                                        {session.ip_address} • {session.device}{' '}
                                        • {session.browser}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-500" data-id="element-727">
                                    {new Date(session.start_time).toLocaleString()}
                                  </div>
                                </div>)}
                          </div>
                        </div>
                      </div>}

                    {activeTab === 'people' && <div className="space-y-4" data-id="element-728">
                        {filteredPeople.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-729">
                            No people data yet
                          </div> : filteredPeople.map(person => <div key={person.person_id} className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700" data-id="element-730">
                              <div className="flex items-start justify-between mb-4" data-id="element-731">
                                <div data-id="element-732">
                                  <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-733">
                                    {person.name || 'Anonymous'}
                                  </h3>
                                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-734">
                                    {person.role}{' '}
                                    {person.company && `at ${person.company}`}
                                  </p>
                                </div>
                                <button onClick={() => setSelectedPerson(selectedPerson === person.person_id ? null : person.person_id)} className="text-red-600 hover:text-red-700 text-sm font-medium" data-id="element-735">
                                  {selectedPerson === person.person_id ? 'Hide Details' : 'View Details'}
                                </button>
                              </div>

                              <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-736">
                                <div data-id="element-737">
                                  <div className="text-gray-500 text-xs" data-id="element-738">
                                    Sessions
                                  </div>
                                  <div className="font-bold text-gray-900 dark:text-white" data-id="element-739">
                                    {person.total_sessions}
                                  </div>
                                </div>
                                <div data-id="element-740">
                                  <div className="text-gray-500 text-xs" data-id="element-741">
                                    Total Time
                                  </div>
                                  <div className="font-bold text-gray-900 dark:text-white" data-id="element-742">
                                    {formatDuration(person.total_time_spent)}
                                  </div>
                                </div>
                                <div data-id="element-743">
                                  <div className="text-gray-500 text-xs" data-id="element-744">
                                    First Seen
                                  </div>
                                  <div className="font-bold text-gray-900 dark:text-white" data-id="element-745">
                                    {new Date(person.first_seen).toLocaleDateString()}
                                  </div>
                                </div>
                                <div data-id="element-746">
                                  <div className="text-gray-500 text-xs" data-id="element-747">
                                    Last Seen
                                  </div>
                                  <div className="font-bold text-gray-900 dark:text-white" data-id="element-748">
                                    {new Date(person.last_seen).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>

                              {selectedPerson === person.person_id && <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-749">
                                  <h4 className="font-bold text-sm mb-2 text-gray-900 dark:text-white" data-id="element-750">
                                    Sessions:
                                  </h4>
                                  <div className="space-y-2" data-id="element-751">
                                    {allData.sessions.filter(s => s.person_id === person.person_id).map(session => <div key={session.session_id} className="text-xs bg-gray-50 dark:bg-slate-900 p-2 rounded" data-id="element-752">
                                          {session.city}, {session.country} •{' '}
                                          {new Date(session.start_time).toLocaleString()}{' '}
                                          • {formatDuration(session.duration)}
                                        </div>)}
                                  </div>
                                </div>}
                            </div>)}
                      </div>}

                    {activeTab === 'sessions' && <div className="space-y-4" data-id="element-753">
                        {filteredSessions.map(session => <div key={session.session_id} className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700" data-id="element-754">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-id="element-755">
                              <div data-id="element-756">
                                <div className="text-gray-500 text-xs mb-1" data-id="element-757">
                                  Location
                                </div>
                                <div className="font-medium text-gray-900 dark:text-white" data-id="element-758">
                                  {session.city}, {session.country}
                                </div>
                              </div>
                              <div data-id="element-759">
                                <div className="text-gray-500 text-xs mb-1" data-id="element-760">
                                  IP Address
                                </div>
                                <div className="font-mono text-xs text-gray-900 dark:text-white" data-id="element-761">
                                  {session.ip_address}
                                </div>
                              </div>
                              <div data-id="element-762">
                                <div className="text-gray-500 text-xs mb-1" data-id="element-763">
                                  Device
                                </div>
                                <div className="font-medium text-gray-900 dark:text-white" data-id="element-764">
                                  {session.device} • {session.browser}
                                </div>
                              </div>
                              <div data-id="element-765">
                                <div className="text-gray-500 text-xs mb-1" data-id="element-766">
                                  Duration
                                </div>
                                <div className="font-medium text-gray-900 dark:text-white" data-id="element-767">
                                  {formatDuration(session.duration)}
                                </div>
                              </div>
                            </div>

                            <div className="mt-4 flex gap-4 text-xs text-gray-600 dark:text-gray-400" data-id="element-768">
                              <span data-id="element-769">Pages: {session.page_views}</span>
                              <span data-id="element-770">Clicks: {session.clicks}</span>
                              <span data-id="element-771">Chats: {session.chat_messages}</span>
                              <span data-id="element-772">Scroll: {session.scroll_depth_max}%</span>
                            </div>
                          </div>)}
                      </div>}

                    {activeTab === 'chats' && <div className="space-y-4" data-id="element-773">
                        {filteredChats.map(chat => <div key={chat.chat_id} className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700" data-id="element-774">
                            <div className="flex items-start gap-4" data-id="element-775">
                              <div className="flex-1" data-id="element-776">
                                <div className="mb-3" data-id="element-777">
                                  <div className="text-xs text-gray-500 mb-1" data-id="element-778">
                                    User Message:
                                  </div>
                                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm text-gray-900 dark:text-white" data-id="element-779">
                                    {chat.user_message}
                                  </div>
                                </div>
                                <div data-id="element-780">
                                  <div className="text-xs text-gray-500 mb-1" data-id="element-781">
                                    AI Response:
                                  </div>
                                  <div className="bg-gray-50 dark:bg-slate-900 p-3 rounded-lg text-sm text-gray-900 dark:text-white" data-id="element-782">
                                    {chat.ai_response}
                                  </div>
                                </div>
                              </div>
                              <div className="text-xs space-y-2" data-id="element-783">
                                <div className={`px-2 py-1 rounded ${chat.sentiment === 'positive' ? 'bg-green-100 text-green-800' : chat.sentiment === 'negative' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-784">
                                  {chat.sentiment}
                                </div>
                                <div className="text-gray-500" data-id="element-785">
                                  {chat.intent}
                                </div>
                                {chat.risk_flags.length > 0 && <div className="bg-red-100 text-red-800 px-2 py-1 rounded" data-id="element-786">
                                    ⚠️ Flagged
                                  </div>}
                              </div>
                            </div>
                            <div className="mt-3 text-xs text-gray-500" data-id="element-787">
                              {new Date(chat.timestamp).toLocaleString()} •
                              Latency: {chat.response_latency}ms
                            </div>
                          </div>)}
                      </div>}

                    {activeTab === 'events' && <div className="space-y-2" data-id="element-788">
                        {allData.events.slice(-100).reverse().map(event => <div key={event.event_id} className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 text-xs" data-id="element-789">
                              <div className="flex items-center justify-between" data-id="element-790">
                                <div className="flex items-center gap-3" data-id="element-791">
                                  <span className="font-mono text-gray-500" data-id="element-792">
                                    {event.event_type}
                                  </span>
                                  <span className="text-gray-400" data-id="element-793">•</span>
                                  <span className="text-gray-600 dark:text-gray-400" data-id="element-794">
                                    {event.page_title}
                                  </span>
                                </div>
                                <span className="text-gray-500" data-id="element-795">
                                  {new Date(event.timestamp).toLocaleTimeString()}
                                </span>
                              </div>
                            </div>)}
                      </div>}
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
};